import classNames from 'classnames';
import { observer } from 'mobx-react';

import { analytics } from '../../services/analytics';
import { useStores } from '../../stores/useStores';

import { BannerButton } from './BannerButton';
import { BannerTitle } from './BannerTitle';
import styles from './LobbyPassBanner.module.scss';
import type { BannerConfig } from './useBannerConfig';
import { useBannerConfig } from './useBannerConfig';
import { useEventRotation } from './useEventRotation';

export const LobbyPassBanner = observer(() => {
  const { bingoStore, subscriptionStore } = useStores();
  const { closestBingoEvent, isFetchingBingoEvents, hasBingoResultToShow, bingoResultToShow, bingoEnabled } =
    bingoStore;

  const config = useBannerConfig({ bingoStore, subscriptionStore });
  const rotationData = useEventRotation();
  const { shouldRotate } = rotationData;

  if (!bingoEnabled || isFetchingBingoEvents || !config) {
    return null;
  }

  if (!closestBingoEvent && !hasBingoResultToShow) {
    return null;
  }

  const openBingoFtue = () => {
    bingoStore.setBingoFtueEntryPoint('cc');
    bingoStore.setShouldShowBingoFtue(true);
  };

  const onButtonClick = (action: BannerConfig['action']) => {
    if (action === 'purchase') {
      bingoStore.openBingoStore();
    } else if (action === 'enter-live') {
      const location = closestBingoEvent?.coinType === 'CASH' ? 'sweeps_bingo' : 'cc_bingo';

      void analytics.sendEvent('enter_live_button_click', {
        screen_name: 'lobby',
        location: location,
        event_action: 'click',
        event_category: 'metagame_bingo',
      });

      void bingoStore.enterLiveGame();
    } else if (action === 'acknowledge-last-result' && bingoResultToShow) {
      const { gameId, totalUserReward, coinType, numOfUserWinningCards } = bingoResultToShow;
      if (gameId && totalUserReward !== undefined && coinType) {
        bingoStore.openScratchCardDialog({
          totalUserReward,
          coinType,
          gameId,
          numOfUserWinningCards,
        });
      }
    } else if (action === 'open-bingo-ftue') {
      openBingoFtue();
    }
  };

  const handleBannerClick = () => {
    if (config.showButton && config.action) {
      onButtonClick(config.action);
    } else if (config.action === 'acknowledge-last-result' && bingoResultToShow?.gameId) {
      void bingoStore.acknowledgeBingoEvent(bingoResultToShow.gameId);
    } else if (config.action === 'open-bingo-ftue') {
      openBingoFtue();
    }
  };

  return (
    <div className={classNames(...config.bannerClasses)} onClick={handleBannerClick}>
      {config.showCheckersBackground && <div className={styles.checkersBackground} />}
      <div className={classNames(styles.bannerContent, { [styles.rotating]: shouldRotate })}>
        <div className={styles.textBlock}>
          <div className={styles.title}>
            <BannerTitle
              bingoResultToShow={bingoResultToShow || undefined}
              config={config}
              hasBingoResultToShow={hasBingoResultToShow}
              rotationData={rotationData}
              showCheckersBackground={config.showCheckersBackground}
            />
          </div>
        </div>
        {config.showButton && (
          <div className={styles.actions}>
            <BannerButton config={config} onButtonClick={onButtonClick} />
          </div>
        )}
      </div>
    </div>
  );
});
